<script setup lang="ts">
import type { EChartsCoreOption } from 'echarts/core'
import ArrowRight from '@/assets/icon/arrow-right.svg'
const matomo = useMatomo()

type MarketMetric = {
  title: string
  value: string
  change: string
  trend: 'up' | 'down'
}

const { localePath, t } = useAppI18n()

const props = defineProps<{
  data?: Record<string, unknown>
}>()

const marketPayload = computed(() => {
  return (props.data as Record<string, unknown> | undefined) ?? {}
})
const totalMarketCapCard = computed(() => {
  return (marketPayload.value.totalMarketCapCard as Record<string, unknown> | undefined) ?? {}
})
const tradeVolume24hCard = computed(() => {
  return (marketPayload.value.tradeVolume24hCard as Record<string, unknown> | undefined) ?? {}
})
const openContractsCard = computed(() => {
  return (marketPayload.value.openContractsCard as Record<string, unknown> | undefined) ?? {}
})
const marketGreedIndexCard = computed(() => {
  return (marketPayload.value.marketGreedIndexCard as Record<string, unknown> | undefined) ?? {}
})
const cryptoSeasonIndexCard = computed(() => {
  return (marketPayload.value.cryptoSeasonIndexCard as Record<string, unknown> | undefined) ?? {}
})

const marketGreedTitle = computed(() => {
  const title = marketGreedIndexCard.value.title
  return typeof title === 'string' && title ? title : 'Market Greed Index'
})
const cryptoSeasonTitle = computed(() => {
  const title = cryptoSeasonIndexCard.value.title
  return typeof title === 'string' && title ? title : 'Crypto Season Index'
})

const GREED_GAUGE_MAX = 100
const GREED_SCORE = computed(() => {
  return typeof marketGreedIndexCard.value.score === 'number' ? marketGreedIndexCard.value.score : 19
})
const greedScoreRatio = computed(() => {
  return Math.min(Math.max(GREED_SCORE.value / GREED_GAUGE_MAX, 0), 1)
})
const GREED_SCORE_LABEL = computed(() => {
  const scoreDesc = marketGreedIndexCard.value.scoreDesc
  return typeof scoreDesc === 'string' && scoreDesc ? scoreDesc : 'Fear'
})
const GREED_GAUGE_DOT_SIZE = 12
const GREED_GAUGE_RADIUS = '93.55%'
const GREED_GAUGE_CENTER_Y = '46.7742%'
const GREED_GAUGE_DOT_CENTER_X = 60.4
const GREED_GAUGE_DOT_CENTER_Y = 5
const GREED_GAUGE_DOT_START_X = 6
const GREED_GAUGE_DOT_START_Y = 58
const GREED_GAUGE_DOT_ARC_CENTER_X = GREED_GAUGE_DOT_CENTER_X
const GREED_GAUGE_DOT_ARC_CENTER_Y = GREED_GAUGE_DOT_START_Y
const GREED_GAUGE_DOT_ARC_RADIUS_X = GREED_GAUGE_DOT_CENTER_X - GREED_GAUGE_DOT_START_X
const GREED_GAUGE_DOT_ARC_RADIUS_Y = GREED_GAUGE_DOT_START_Y - GREED_GAUGE_DOT_CENTER_Y
const GREED_GAUGE_LINE_WIDTH = 7.44
const SEASON_SCORE = computed(() => {
  return typeof cryptoSeasonIndexCard.value.indexValue === 'number' ? cryptoSeasonIndexCard.value.indexValue : 22
})
const SEASON_SCORE_MAX = computed(() => {
  return typeof cryptoSeasonIndexCard.value.indexMax === 'number' ? cryptoSeasonIndexCard.value.indexMax : 100
})
// 加密季节指数分段条颜色，与数据页保持一致。
const SEASON_SEGMENT_COLORS = ['#E2483D', '#FFD5D2', '#CFE1FD', '#1868DB'] as const
/**
 * 加密季节指数分段边界。
 * 0-25 对应首段红色，26-50、51-75、76-100 分别对应其余三段颜色。
 */
const SEASON_SEGMENT_BOUNDARIES = [25.99, 49.99, 74.99] as const
const seasonLeftLabel = computed(() => {
  const label = cryptoSeasonIndexCard.value.leftLabel
  return typeof label === 'string' && label ? label : 'Bit'
})
const seasonRightLabel = computed(() => {
  const label = cryptoSeasonIndexCard.value.rightLabel
  return typeof label === 'string' && label ? label : 'Alt'
})

const greedGaugeRef = ref<HTMLElement | null>(null)
const marketSectionRef = ref<HTMLElement | null>(null)

const metricValueProgress = ref(0)
const metricChangeProgress = ref(0)
const greedPointerProgress = ref(0)
const seasonBarProgress = ref(1)
const seasonScoreProgress = ref(0)
const seasonIndicatorProgress = ref(0)
const hasChangeEntered = ref(false)

const { init } = useEcharts()

let greedGaugeChart: ReturnType<typeof init> | null = null
let marketObserver: IntersectionObserver | null = null
let hasIntroPlayed = false
let hasIntroScheduled = false
const INTRO_ANIMATION_TRIGGER_DELAY = 500

/** 优先按涨跌幅文案的正负判断趋势，0 值按下跌展示，接口类型仅作为兜底。 */
const resolveMetricTrend = (card: Record<string, unknown>) => {
  const changeText = typeof card.changeText === 'string' ? card.changeText : ''
  const changeValue = Number(changeText.replace(/[,%\s+]/g, ''))

  if (Number.isFinite(changeValue)) {
    return changeValue <= 0 ? 'down' : 'up'
  }

  const changeType = typeof card.changeType === 'number' ? card.changeType : undefined
  return changeType === 2 ? 'down' : 'up'
}

const primaryMetrics = computed<MarketMetric[]>(() => {
  const totalCard = totalMarketCapCard.value
  const volumeCard = tradeVolume24hCard.value
  const contractCard = openContractsCard.value

  return [
    {
      title: typeof totalCard.title === 'string' && totalCard.title ? totalCard.title : 'Total Market Cap',
      value: typeof totalCard.valueText === 'string' && totalCard.valueText ? totalCard.valueText : '$3.946T',
      change: typeof totalCard.changeText === 'string' && totalCard.changeText ? totalCard.changeText : '5%',
      trend: resolveMetricTrend(totalCard),
    },
    {
      title: typeof volumeCard.title === 'string' && volumeCard.title ? volumeCard.title : '24h Trade Volume',
      value: typeof volumeCard.valueText === 'string' && volumeCard.valueText ? volumeCard.valueText : '$1.296T',
      change: typeof volumeCard.changeText === 'string' && volumeCard.changeText ? volumeCard.changeText : '3%',
      trend: resolveMetricTrend(volumeCard),
    },
    {
      title: typeof contractCard.title === 'string' && contractCard.title ? contractCard.title : 'Open Contracts',
      value: typeof contractCard.valueText === 'string' && contractCard.valueText ? contractCard.valueText : '$786.2M',
      change: typeof contractCard.changeText === 'string' && contractCard.changeText ? contractCard.changeText : '12%',
      trend: resolveMetricTrend(contractCard),
    },
  ]
})

type ParsedMetricValue = {
  amount: number
  decimals: number
  suffix: string
}

type ParsedMetricChange = {
  amount: number
  decimals: number
  prefix: string
  suffix: string
  rawText: string
}

const parseMetricValue = (value: string): ParsedMetricValue => {
  const sanitized = value.replaceAll(',', '').trim()
  const matched = /^\$?(\d+(?:\.\d+)?)([A-Za-z]+)?$/.exec(sanitized)

  if (!matched) {
    return {
      amount: 0,
      decimals: 0,
      suffix: '',
    }
  }

  const amountText = matched[1]
  const suffix = matched[2] ?? ''
  const decimals = amountText.includes('.') ? (amountText.split('.')[1]?.length ?? 0) : 0

  return {
    amount: Number(amountText),
    decimals,
    suffix,
  }
}

const formatMetricValue = (amount: number, decimals: number, suffix: string) => {
  return `$${amount.toFixed(decimals)}${suffix}`
}

const parseMetricChange = (change: string): ParsedMetricChange => {
  const rawText = change.trim()
  const matched = /^([+-]?)(\d+(?:\.\d+)?)(.*)$/.exec(rawText)

  if (!matched) {
    return {
      amount: 0,
      decimals: 0,
      prefix: '',
      suffix: rawText.includes('%') ? '%' : '',
      rawText,
    }
  }

  const amountText = matched[2]

  return {
    amount: Number(amountText),
    decimals: amountText.includes('.') ? (amountText.split('.')[1]?.length ?? 0) : 0,
    prefix: matched[1],
    suffix: matched[3] || '%',
    rawText,
  }
}

const formatMetricChange = (change: ParsedMetricChange, progress: number) => {
  if (progress >= 1) {
    return change.rawText
  }

  return `${change.prefix}${(change.amount * progress).toFixed(change.decimals)}${change.suffix}`
}

const easeOutCubic = (value: number) => {
  return 1 - Math.pow(1 - value, 3)
}

const parsedPrimaryMetricValues = computed(() => {
  return primaryMetrics.value.map((metric) => parseMetricValue(metric.value))
})
const parsedPrimaryMetricChanges = computed(() => {
  return primaryMetrics.value.map((metric) => parseMetricChange(metric.change))
})

const animatedPrimaryMetricValues = computed(() => {
  return parsedPrimaryMetricValues.value.map((metric) => {
    return formatMetricValue(metric.amount * metricValueProgress.value, metric.decimals, metric.suffix)
  })
})

const animatedPrimaryMetricChanges = computed(() => {
  return parsedPrimaryMetricChanges.value.map((change) => {
    return formatMetricChange(change, metricChangeProgress.value)
  })
})

const animatedGreedScore = computed(() => {
  return Math.round(GREED_SCORE.value * greedPointerProgress.value)
})

const animatedSeasonScore = computed(() => {
  return Math.round(SEASON_SCORE.value * seasonScoreProgress.value)
})

const seasonBarAnimatedStyle = computed(() => {
  return {
    width: `${Math.min(Math.max(seasonBarProgress.value, 0), 1) * 100}%`,
  }
})

const metricValueAnimatedStyle = computed(() => {
  return {
    opacity: `${0.32 + metricValueProgress.value * 0.68}`,
    transform: `scale(${0.94 + metricValueProgress.value * 0.06})`,
    filter: `blur(${(1 - metricValueProgress.value) * 1.4}px)`,
  }
})

const metricChangeAnimatedStyle = computed(() => {
  return {
    opacity: `${0.2 + metricChangeProgress.value * 0.8}`,
    transform: `translateY(${(1 - metricChangeProgress.value) * 8}px)`,
  }
})

const seasonScoreAnimatedStyle = computed(() => {
  return {
    opacity: `${0.2 + seasonScoreProgress.value * 0.8}`,
  }
})

// 分数和最大值共用同一行位移动画，避免仅移动分数数字导致 baseline 错位。
const seasonScoreRowAnimatedStyle = computed(() => {
  return {
    transform: `translateY(${(1 - seasonScoreProgress.value) * 8}px)`,
  }
})

// 加密季节指数箭头定位样式，随动画进度偏移。
const seasonIndicatorAnimatedStyle = computed(() => {
  const ratio = Math.min(Math.max(SEASON_SCORE.value / SEASON_SCORE_MAX.value, 0), 1)
  const segmentIndex = SEASON_SEGMENT_BOUNDARIES.findIndex((boundary) => SEASON_SCORE.value <= boundary)
  const indicatorColor = SEASON_SEGMENT_COLORS[segmentIndex === -1 ? SEASON_SEGMENT_COLORS.length - 1 : segmentIndex]

  return {
    left: `${ratio * seasonIndicatorProgress.value * 100}%`,
    opacity: `${seasonIndicatorProgress.value}`,
    'border-bottom': `6px solid ${indicatorColor}`,
  }
})

const activeRafIds = new Set<number>()
const activeTimeoutIds = new Set<ReturnType<typeof setTimeout>>()

/** 市场数据卡片点击后进入当前语言下的数据页，不使用声明式链接暴露路由。 */
const goMarketDataPage = async () => {
  matomo.trackEvent('home_market_data', {})
  await navigateTo(localePath('/data'), {
    open: {
      target: '_blank',
    },
  })
}

const getGreedDotPosition = (progress: number) => {
  const normalized = Math.min(Math.max(progress, 0), 1)
  const angle = normalized * (Math.PI / 2)

  return {
    x: GREED_GAUGE_DOT_ARC_CENTER_X - GREED_GAUGE_DOT_ARC_RADIUS_X * Math.cos(angle),
    y: GREED_GAUGE_DOT_ARC_CENTER_Y - GREED_GAUGE_DOT_ARC_RADIUS_Y * Math.sin(angle),
  }
}

const getGreedGaugeOption = (): EChartsCoreOption => {
  const dotPosition = getGreedDotPosition(greedPointerProgress.value * greedScoreRatio.value)

  return {
    animation: false,
    series: [
      {
        type: 'gauge',
        silent: true,
        min: 0,
        max: GREED_GAUGE_MAX,
        startAngle: 180,
        endAngle: 0,
        center: ['50%', GREED_GAUGE_CENTER_Y],
        radius: GREED_GAUGE_RADIUS,
        pointer: {
          show: false,
        },
        anchor: {
          show: false,
        },
        progress: {
          show: true,
          width: GREED_GAUGE_LINE_WIDTH,
          roundCap: true,
          clip: false,
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 1,
              y2: 0,
              colorStops: [
                { offset: 0, color: '#E2483D' },
                { offset: 1, color: '#F8F8F8' },
              ],
            },
          },
        },
        axisLine: {
          roundCap: true,
          lineStyle: {
            width: GREED_GAUGE_LINE_WIDTH,
            color: [[1, 'transparent']],
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        detail: {
          show: false,
        },
        title: {
          show: false,
        },
        data: [{ value: GREED_GAUGE_MAX }],
      },
    ],
    graphic: [
      {
        type: 'circle',
        shape: {
          cx: dotPosition.x,
          cy: dotPosition.y,
          r: GREED_GAUGE_DOT_SIZE / 2,
        },
        style: {
          fill: '#292A2E',
          stroke: '#FFFFFF',
          lineWidth: 2,
        },
        z: 100,
        zlevel: 1,
        silent: true,
      },
    ],
  }
}

const runTween = (
  duration: number,
  update: (value: number) => void,
  options: {
    delay?: number
    easing?: (value: number) => number
    onComplete?: () => void
  } = {},
) => {
  const { delay = 0, easing = easeOutCubic, onComplete } = options

  const launch = () => {
    let startAt = 0
    let rafId = 0

    const step = (timestamp: number) => {
      if (!startAt) {
        startAt = timestamp
      }

      const elapsed = timestamp - startAt
      const progress = Math.min(elapsed / duration, 1)
      update(easing(progress))

      if (progress < 1) {
        rafId = window.requestAnimationFrame(step)
        activeRafIds.add(rafId)
      } else {
        activeRafIds.delete(rafId)
        onComplete?.()
      }
    }

    rafId = window.requestAnimationFrame(step)
    activeRafIds.add(rafId)
  }

  if (!delay) {
    launch()
    return
  }

  const timeoutId = window.setTimeout(() => {
    activeTimeoutIds.delete(timeoutId)
    launch()
  }, delay)

  activeTimeoutIds.add(timeoutId)
}

const clearIntroAnimation = () => {
  activeRafIds.forEach((rafId) => {
    window.cancelAnimationFrame(rafId)
  })
  activeRafIds.clear()

  activeTimeoutIds.forEach((timeoutId) => {
    clearTimeout(timeoutId)
  })
  activeTimeoutIds.clear()
}

const renderGreedGauge = () => {
  if (!greedGaugeChart) {
    return
  }

  greedGaugeChart.setOption(getGreedGaugeOption(), {
    notMerge: true,
    lazyUpdate: true,
  })
}

const startIntroAnimation = () => {
  if (hasIntroPlayed) {
    return
  }

  hasIntroScheduled = false
  hasIntroPlayed = true

  metricValueProgress.value = 0
  metricChangeProgress.value = 0
  greedPointerProgress.value = 0
  // 分段进度条初始化即展示完整状态，仅指针保留入场动画。
  seasonBarProgress.value = 1
  seasonScoreProgress.value = 0
  seasonIndicatorProgress.value = 0
  hasChangeEntered.value = false

  renderGreedGauge()

  runTween(1000, (value) => {
    metricValueProgress.value = value
  })

  runTween(
    1000,
    (value) => {
      metricChangeProgress.value = value
    },
    {
      delay: 80,
      onComplete: () => {
        hasChangeEntered.value = true
      },
    },
  )

  runTween(
    600,
    (value) => {
      greedPointerProgress.value = value
      renderGreedGauge()
    },
    {
      delay: 120,
    },
  )

  runTween(
    720,
    (value) => {
      seasonScoreProgress.value = value
    },
    {
      delay: 140,
    },
  )

  runTween(
    720,
    (value) => {
      seasonIndicatorProgress.value = value
    },
    {
      delay: 140,
    },
  )
}

const scheduleIntroAnimation = (delay = INTRO_ANIMATION_TRIGGER_DELAY) => {
  if (hasIntroPlayed || hasIntroScheduled) {
    return
  }

  hasIntroScheduled = true

  const timeoutId = window.setTimeout(() => {
    activeTimeoutIds.delete(timeoutId)

    window.requestAnimationFrame(() => {
      startIntroAnimation()
    })
  }, delay)

  activeTimeoutIds.add(timeoutId)
}

const initGreedGauge = () => {
  if (!greedGaugeRef.value) {
    return
  }

  greedGaugeChart = init(greedGaugeRef.value, { lazy: true })
  renderGreedGauge()
}

const onResize = () => {
  if (!greedGaugeChart) {
    return
  }

  greedGaugeChart.resize()
  renderGreedGauge()
}

onMounted(() => {
  initGreedGauge()
  window.addEventListener('resize', onResize)

  if (!marketSectionRef.value) {
    scheduleIntroAnimation()
    return
  }

  if (!('IntersectionObserver' in window)) {
    scheduleIntroAnimation()
    return
  }

  marketObserver = new IntersectionObserver(
    (entries) => {
      const [entry] = entries

      if (!entry?.isIntersecting || hasIntroPlayed) {
        return
      }

      scheduleIntroAnimation()
      marketObserver?.disconnect()
      marketObserver = null
    },
    {
      threshold: 0.35,
    },
  )

  marketObserver.observe(marketSectionRef.value)
})

onBeforeUnmount(() => {
  clearIntroAnimation()
  marketObserver?.disconnect()
  marketObserver = null
  window.removeEventListener('resize', onResize)
  greedGaugeChart?.dispose()
  greedGaugeChart = null
})
</script>

<template>
  <section ref="marketSectionRef" class="market" aria-label="Market Data">
    <div class="market-header">
      <h2 class="market-title">{{ t('82501') }}</h2>
      <NuxtLink
        :to="localePath('/data')"
        target="_blank"
        class="market-arrow-btn rtl:rotate-180 hover:[&_.market-arrow-icon_path]:!fill-[#292A2E]"
        aria-label="View more market data"
      >
        <ArrowRight class="market-arrow-icon size-[24px]" aria-hidden="true" />
      </NuxtLink>
    </div>

    <div class="market-grid">
      <article
        v-for="(metric, index) in primaryMetrics"
        :key="metric.title"
        class="market-card market-card-metric cursor-pointer hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
        @click="goMarketDataPage"
      >
        <h3 class="metric-title">{{ metric.title }}</h3>
        <div class="metric-value-wrap">
          <p class="metric-value" :style="metricValueAnimatedStyle">{{ animatedPrimaryMetricValues[index] }}</p>
          <div
            class="metric-change"
            :class="[metric.trend === 'up' ? 'is-up' : 'is-down', { 'is-change-ready': hasChangeEntered }]"
            :style="metricChangeAnimatedStyle"
          >
            <span>{{ animatedPrimaryMetricChanges[index] }}</span>
            <span class="trend-icon" aria-hidden="true">{{ metric.trend === 'up' ? '↑' : '↓' }}</span>
          </div>
        </div>
      </article>

      <article
        class="market-card market-card-greed cursor-pointer hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
        @click="goMarketDataPage"
      >
        <h3 class="metric-title">{{ marketGreedTitle }}</h3>
        <div class="greed-chart-wrap" aria-label="Market greed gauge">
          <div ref="greedGaugeRef" class="greed-gauge-chart" aria-hidden="true" />
          <div class="greed-score">
            <p class="greed-score-value">{{ animatedGreedScore }}</p>
            <p class="greed-score-label">{{ GREED_SCORE_LABEL }}</p>
          </div>
        </div>
      </article>

      <article
        class="market-card market-card-season cursor-pointer hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
        @click="goMarketDataPage"
      >
        <h3 class="metric-title">{{ cryptoSeasonTitle }}</h3>
        <div class="season-wrap">
          <div class="season-label-row">
            <span>{{ seasonLeftLabel }}</span>
            <span>{{ seasonRightLabel }}</span>
          </div>
          <div class="season-bars-track" aria-hidden="true">
            <span class="season-indicator" :style="seasonIndicatorAnimatedStyle"></span>
            <span class="season-bars" :style="seasonBarAnimatedStyle" />
          </div>
          <div class="season-score-row" :style="seasonScoreRowAnimatedStyle">
            <span class="season-score" :style="seasonScoreAnimatedStyle">{{ animatedSeasonScore }}</span>
            <span class="season-max">/{{ SEASON_SCORE_MAX }}</span>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped lang="less">
.market {
  position: relative;
  z-index: 0;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 50%;
    bottom: 0;
    left: 50%;
    width: 100vw;
    transform: translateX(-50%);
    background: #f8f8f8;
    z-index: -1;
  }

  .market-header {
    width: 100%;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .market-title {
      margin: 0;
      color: #292a2e;
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
    }

    .market-arrow-btn {
      border: none;
      border-radius: 4px;
      background: transparent;
      padding: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      .market-arrow-icon {
        width: 24px;
        height: 24px;
      }
    }
  }

  .market-grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 16px;
  }

  .market-card {
    height: 138px;
    border-radius: 8px;
    background: #fff;
    padding: 20px 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transform: translateY(0) scale(1);
    transform-origin: center center;
    transition:
      transform 0.24s cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
    will-change: transform, box-shadow;

    &:hover {
      transform: translateY(-4px) scale(1);
    }
  }

  .metric-title {
    width: 100%;
    margin: 0;
    color: #505258;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    flex-shrink: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .metric-value-wrap {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .metric-value {
    margin: 0;
    color: #292a2e;
    font-size: 32px;
    font-weight: 700;
    line-height: 38px;
    white-space: nowrap;
    transform-origin: left center;
    will-change: transform, opacity, filter;
  }

  .metric-change {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    will-change: transform, opacity;

    &.is-up {
      color: #22a06b;

      .trend-icon {
        --trend-icon-rotation: 180deg;
        --fill-0: #22a06b;
      }
    }

    &.is-down {
      color: #e2483d;

      .trend-icon {
        --trend-icon-rotation: 0deg;
        --fill-0: #e2483d;
      }
    }

    .trend-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      transform: rotate(var(--trend-icon-rotation, 0deg));
    }

    &.is-change-ready {
      .trend-icon {
        animation: trend-float 1.1s ease-in-out infinite;
      }
    }
  }

  .market-card-greed {
    gap: 12px;

    .greed-chart-wrap {
      position: relative;
      width: 100%;
      height: 62px;
      overflow: hidden;

      .greed-gauge-chart {
        position: absolute;
        top: 0;
        left: 50%;
        width: 124px;
        height: 124px;
        transform: translateX(-50%);
      }

      .greed-score {
        position: absolute;
        left: 50%;
        bottom: 0;
        transform: translateX(-50%);
        display: flex;
        flex-direction: column;
        align-items: center;

        .greed-score-value {
          margin: 0;
          color: #292a2e;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
          text-align: center;
        }

        .greed-score-label {
          margin: 0;
          color: #8c8f97;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
        }
      }
    }
  }

  .market-card-season {
    gap: 12px;

    .season-wrap {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .season-label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #505258;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
      }

      .season-bars {
        width: 0;
        height: 6px;
        display: block;
      }

      .season-bars-track {
        position: relative;
        width: 100%;
        height: 10px;
      }

      .season-indicator {
        position: absolute;
        top: 9px;
        left: 0;
        width: 0;
        height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        transform: translateX(-50%);
        transition: opacity 0.2s ease;
        pointer-events: none;
        will-change: left, opacity;
      }

      .season-score-row {
        display: flex;
        align-items: baseline;
        gap: 2px;

        .season-score {
          color: #292a2e;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
        }

        .season-max {
          color: #8c8f97;
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }
      }
    }
  }
}

@keyframes trend-float {
  0% {
    transform: translateY(0) rotate(var(--trend-icon-rotation, 0deg));
  }

  50% {
    transform: translateY(-2px) rotate(var(--trend-icon-rotation, 0deg));
  }

  100% {
    transform: translateY(0) rotate(var(--trend-icon-rotation, 0deg));
  }
}

@media screen and (max-width: 1200px) and (min-width: 801px) {
  .market {
    width: calc(100% - 48px);
  }
}

@media screen and (max-width: 920px) {
  .market {
    .market-grid {
      grid-template-columns: repeat(6, minmax(0, 1fr));

      .market-card {
        grid-column: span 2;

        &:nth-child(4) {
          grid-column: 1 / span 3;
        }

        &:nth-child(5) {
          grid-column: 4 / span 3;
        }
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .market {
    width: 800px;
    min-width: 800px;
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .market {
    .market-card {
      transition: none;
    }
  }
}
</style>
